import { useEffect } from "react";
import { GameCanvas } from "./components/GameCanvas";
import { BettingPanel } from "./components/BettingPanel";
import { LoginModal } from "./components/LoginModal";
import { useGameStore } from "./store/gameStore";
import { socketManager } from "./services/socket";
import "./App.css";

/**
 * MAIN APPLICATION COMPONENT
 */

function App() {
  const isAuthenticated = useGameStore((state) => state.isAuthenticated);
  const accessToken = useGameStore((state) => state.accessToken);
  const user = useGameStore((state) => state.user);
  const connectionState = useGameStore((state) => state.connectionState);

  // Setup socket connection
  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;

    socketManager.connect(accessToken);

    // Setup event listeners
    const unsubscribe = [
      // Connection events
      socketManager.on("connect", () => {
        useGameStore.getState().setConnectionState("connected");
      }),

      socketManager.on("disconnect", () => {
        useGameStore.getState().setConnectionState("disconnected");
      }),

      socketManager.on("connect_error", () => {
        useGameStore.getState().setConnectionState("error");
      }),

      // Round events
      socketManager.on("round_state", (data) => {
        useGameStore.getState().updateRound({
          roundId: data.roundId,
          roundNumber: data.roundNumber,
          phase: data.phase as "BETTING" | "RUNNING" | "CRASHED" | "COMPLETE",
          commitment: data.commitment,
          currentMultiplier: data.currentMultiplier || 1.0,
          bettingStartTime: data.bettingStartTime,
          bettingDuration: (data as any).bettingDuration,
          startTime: data.startTime,
        });
      }),

      socketManager.on("round_started", (data) => {
        useGameStore.getState().updateRound({
          roundId: data.roundId,
          roundNumber: data.roundNumber,
          phase: "BETTING",
          commitment: data.commitment,
          currentMultiplier: 1.0,
          bettingStartTime: data.bettingStartTime,
          bettingDuration: data.bettingDuration,
        });
      }),

      socketManager.on("round_running", (data) => {
        useGameStore.getState().updateRound({
          phase: "RUNNING",
          startTime: data.startTime,
        });

        // Activate current bet
        const currentBet = useGameStore.getState().currentBet;
        if (currentBet && currentBet.status === "pending") {
          useGameStore.getState().placeBet({
            ...currentBet,
            status: "active",
          });
        }
      }),

      socketManager.on("multiplier_update", (data) => {
        useGameStore.getState().updateMultiplier(data.multiplier);
      }),

      socketManager.on("round_crashed", (data) => {
        useGameStore.getState().crashRound(data.crashPoint, data.crashTime);
      }),

      socketManager.on("round_complete", (data) => {
        useGameStore.getState().addRoundToHistory({
          id: data.roundId,
          roundNumber: useGameStore.getState().currentRound?.roundNumber || 0,
          crashPointRevealed: data.crashPoint,
          createdAt: new Date().toISOString(),
        });
        // Clear bet for next round
        useGameStore.getState().clearBet();
      }),

      // Bet events
      socketManager.on("bet_placed", (data) => {
        useGameStore.getState().placeBet({
          betId: data.betId,
          amount: data.amount,
          autoCashoutAt: data.autoCashoutAt,
          status: "pending",
        });
        useGameStore.getState().updateBalance(data.balance);
      }),

      socketManager.on("cashout_success", (data) => {
        useGameStore.getState().cashoutBet(data);
      }),

      socketManager.on("player_bet", (data) => {
        useGameStore.getState().addPlayerActivity({
          username: data.username,
          action: "bet",
          amount: data.amount,
        });
      }),

      socketManager.on("player_cashed_out", (data) => {
        useGameStore.getState().addPlayerActivity({
          username: data.username,
          action: "cashout",
          amount: data.payout,
          multiplier: data.multiplier,
        });
      }),

      // Balance updates
      socketManager.on("balance_update", (data) => {
        useGameStore.getState().updateBalance(data.balance);
      }),

      // Errors
      socketManager.on("error", (data) => {
        useGameStore.getState().setError(data.message);
      }),
    ];

    return () => {
      unsubscribe.forEach((fn) => fn());
      socketManager.disconnect();
    };
  }, [isAuthenticated, accessToken]);

  // Auto-login if token exists
  useEffect(() => {
    if (accessToken && !user) {
      // Fetch user profile
      fetch(
        `${
          import.meta.env.VITE_API_URL || "http://localhost:3000"
        }/api/user/profile`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )
        .then((res) => res.json())
        .then((data) => {
          useGameStore.getState().setAuth(
            {
              accessToken,
              refreshToken: useGameStore.getState().refreshToken || "",
            },
            {
              id: data.id,
              username: data.username,
              balance:
                typeof data.balance === "string"
                  ? parseFloat(data.balance)
                  : data.balance,
            },
          );
        })
        .catch(() => {
          useGameStore.getState().logout();
        });
    }
  }, [accessToken, user]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Login Modal */}
      <LoginModal />

      {/* Header */}
      <header className="bg-slate-800/50 backdrop-blur-sm border-b border-slate-700">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-primary-400 to-primary-600 bg-clip-text text-transparent">
            🚀 Crash Game
          </h1>

          <div className="flex items-center gap-4">
            {/* Connection Status */}
            <div className="flex items-center gap-2">
              <div
                className={`w-2 h-2 rounded-full ${
                  connectionState === "connected"
                    ? "bg-success animate-pulse"
                    : connectionState === "connecting"
                    ? "bg-warning animate-pulse"
                    : connectionState === "error"
                    ? "bg-danger animate-pulse"
                    : "bg-gray-500"
                }`}
              />
              <span className="text-sm text-gray-400">{connectionState}</span>
            </div>

            {user ? (
              <>
                <span className="text-primary-400 font-semibold">
                  {user.username}
                </span>
                <button
                  onClick={() => {
                    useGameStore.getState().logout();
                    socketManager.disconnect();
                  }}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition"
                >
                  Logout
                </button>
              </>
            ) : (
              <button
                onClick={() => useGameStore.getState().setShowLoginModal(true)}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg transition"
              >
                Login
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Game Canvas */}
          <div className="lg:col-span-2 h-[500px]">
            <GameCanvas />
          </div>

          {/* Betting Panel */}
          <div>
            <BettingPanel />
          </div>
        </div>

        {/* Round History */}
        <div className="mt-8 bg-slate-800 rounded-lg p-6">
          <h2 className="text-xl font-bold mb-4">Recent Rounds</h2>
          <div className="flex gap-2 flex-wrap">
            {useGameStore((state) => state.roundHistory).map((round) => (
              <div
                key={round.id}
                className={`px-4 py-2 rounded-lg font-bold ${
                  round.crashPointRevealed >= 2
                    ? "bg-success/20 text-success"
                    : round.crashPointRevealed >= 1.5
                    ? "bg-primary-500/20 text-primary-400"
                    : "bg-danger/20 text-danger"
                }`}
              >
                {round.crashPointRevealed.toFixed(2)}x
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;
